/* eslint-disable quote-props,jsx-a11y/label-has-for */

import React, {Fragment, memo, useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import getPath from '@neonaut/lib-js/es/object/getPath';

import {translate} from '@mapsight/ui/src/js/helpers/i18n';
import modClasses from '@mapsight/ui/src/js/helpers/mod-classes';

import {nonEmptyString} from '../../modules/helpers';
import {useNavPos} from '../../hooks';

import {
	captchaSelector,
	storageAuthorSelector,
	storageSelector,
	storageTokenSelector
} from '../store/selectors';
import {get as getRating, getCaptcha, putRating} from '../store/actions';
import {SendingState} from '../store/controller';

import StarsField from './stars-field';


/** @typedef {{type: "init"}
 * | {type: "sending"}
 * | {type: "sent"}
 * | {type: "error", message: string}} FormSendState */

export default memo(
	function RatingForm() {
		const dispatch = useDispatch();
		const navPos = useNavPos();

		const {error: captchaError, question: captchaQuestion} = useSelector(captchaSelector);
		const storageAuthor = useSelector(storageAuthorSelector);
		const token = useSelector(storageTokenSelector);

		// das useMemo ist nur dafür da, dass der von uri und deepLink abhöngende ratingData-Selector tatsächlich neu berechnet wird, wenn sich was ändert
		// dadurch kann in der nächsten Zeile einfach ein useSelector(…poiSubselector…) stehen
		const poiSubselector = useMemo(
			() =>
				(
					navPos.app?.uri
					&& navPos.deepLink ?
						_ => getPath(_, [navPos.app.uri, navPos.deepLink], {}) :
						_ => {}
				),
			[navPos.app, navPos.deepLink]
		);
		const {ratingData = {}, state: sendState} = useSelector(_ => poiSubselector(storageSelector(_)?.features));

		const sourceId = navPos.app?.uri;
		const poiId = navPos.deepLink;
		const ratingApiEndpoint = !navPos.app?.url || !poiId  ? null :
			`${navPos.app.url}/${poiId}/rating`;

		useEffect(
			() => {
				if (!ratingApiEndpoint) {
					return;
				}
				dispatch(getCaptcha(ratingApiEndpoint));
			},
			[ratingApiEndpoint, dispatch],
		);

		useEffect(
			() => {
				if (!ratingApiEndpoint ) {
					return;
				}
				dispatch(getRating(ratingApiEndpoint, token, navPos.app.uri, navPos.deepLink));
			},
			[ratingApiEndpoint, token, navPos.app, navPos.deepLink, dispatch],
		);


		// Form data input handling
		const [dirty, setDirty] = useState(false);
		useEffect(() => setDirty(false), [ratingData]);

		const [author, setAuthor] = useState(storageAuthor || '');
		const onAuthorChange = useCallback(
			/** @param {React.ChangeEvent<HTMLInputElement>} ev event */
			(ev) => {
				setAuthor(ev.target.value);
				setDirty(true);
			},
			[],
		);
		const [captchaAnswer, setCaptchaAnswer] = useState('');
		const onCaptchaAnswerChange = useCallback(
			/** @param {React.ChangeEvent<HTMLInputElement>} ev event */
			(ev) => {
				setCaptchaAnswer(ev.target.value); // dass hier ein String gesetzt wird, damit muss der PHP-Code klar kommen
			},
			[],
		);

		const [rating, setRating] = useState(/** @type {null | number} */(ratingData.rating));
		const onSetRating = useCallback(
			(value) => {
				setRating(value);
				setDirty(true);
			},
			[],
		);
		const [comment, setComment] = useState(ratingData.comment || '');
		const onCommentChange = useCallback(
			/** @param {React.ChangeEvent<HTMLInputElement>} ev event */
			(ev) => {
				setComment(ev.target.value);
				setDirty(true);
			},
			[],
		);

		const submitEnabled = !!(
			ratingApiEndpoint
			&& sendState !== SendingState.SENDING
			&& rating
			&& nonEmptyString(author) // @thomas: das ursprügliche author === "" hätte wäre bei null falsch gewesen und hätte den Code weiterlaufen lassen, was hier gar keinen Sinn macht
			&& nonEmptyString(comment) // daher bitte meisten einfach mit der Negation auf leere Variablen testen, das schließt leere String mit ein
			&& nonEmptyString(captchaAnswer) // die captchaAnswer ist ein String und "0" ist true (anders als die Zahl 0)
			&& !captchaError
			&& nonEmptyString(captchaQuestion)
		);
		const captchaRetryEnabled = !submitEnabled && (captchaError || !captchaQuestion);
		const onSubmit = useCallback(
			/** @param {React.FormEvent<HTMLFormElement>} ev event */
			(ev) => {
				ev.preventDefault();
				ev.stopPropagation();

				if(captchaRetryEnabled) {
					setCaptchaAnswer('');
					dispatch(getCaptcha(ratingApiEndpoint));
					return;
				} else if (!submitEnabled) {
					return;
				}

				dispatch(putRating(
					ratingApiEndpoint,
					nonEmptyString(captchaAnswer),
					sourceId,
					poiId,
					rating,
					ratingApiEndpoint.startsWith('/en') ? 'de' : 'en',
					nonEmptyString(comment),
					nonEmptyString(author),
					token || undefined,
				));
			},
			[
				submitEnabled,
				captchaRetryEnabled,
				ratingApiEndpoint,
				captchaAnswer,
				sourceId,
				poiId,
				rating,
				comment,
				author,
				dispatch,
				token,
			],
		);

		useEffect(
			() => setCaptchaAnswer(''),
			[captchaQuestion]
		);

		let sendingText = '';
		if(!dirty) {
			switch (sendState) {
				case SendingState.SENDING:
					sendingText = translate('ratings.state.submitting');
					break;
				case SendingState.SENT:
					sendingText = translate('ratings.state.submitted');
					break;
				case SendingState.FAILED:
					sendingText = translate('ratings.state.failed');
					break;
				case SendingState.WRONG_CAPTCHA:
					sendingText = translate('ratings.state.wrong-captcha');
					break;
				// default: sendingText bleibt leer
			}
		}

		return (<Fragment>
			<h3>{translate('ratings.showform')}</h3>
			<div className="stade-stars-form">
				<div className={modClasses(
					'stade-stars-form__results',
					sendState === SendingState.FAILED || sendState === SendingState.WRONG_CAPTCHA ? 'error' : 'good')
				}>
					{sendingText}<br/>
				</div>

				<form onSubmit={onSubmit}>
					<div
						className={modClasses('stade-stars-form__field', {
							rating: true,
							error: !rating,
						})}
					>
						<span className="stade-stars-form__field-input">
							<StarsField
								value={rating}
								setRatingCallback={onSetRating}
							/>
						</span>
					</div>

					<label className={modClasses('stade-stars-form__field', 'author')}>
						<span className="visuallyhidden">
							{translate('ratings.author')}:
						</span>

						<input
							className="stade-stars-form__field-input"
							type="text"
							value={author}
							onChange={onAuthorChange}
							required={!captchaRetryEnabled}
							placeholder={author ? undefined : translate('ratings.author')}
						/>
					</label>

					<label className={modClasses('stade-stars-form__field', 'comment')}>
						<span className="visuallyhidden">
							{translate('ratings.comment')}:
						</span>

						<input
							className="stade-stars-form__field-input"
							type="text"
							value={comment}
							onChange={onCommentChange}
							required={!captchaRetryEnabled}
							placeholder={comment ? undefined : translate('ratings.comment')}
						/>
					</label>

					<label className={modClasses('stade-stars-form__field', 'captcha')}>
						<span className="visuallyhidden">
							{translate('ratings.captcha')}:
						</span>

						<div className="stade-stars-form__captcha-content">
							{captchaError &&
								<span>
									{translate('ratings.captcha-loading-error')}
								</span>
							}
							{!captchaError && !captchaQuestion &&
								<span>
									{translate('ratings.loading')}
								</span>
							}
							{!captchaError && captchaQuestion && (
								<span>
									{translate('ratings.captcha-prefix')} {captchaQuestion}:
								</span>
							)}
						</div>

						<input
							className="stade-stars-form__field-input"
							type="number"
							value={captchaAnswer}
							onChange={onCaptchaAnswerChange}
							required={!captchaRetryEnabled}
							placeholder={translate('ratings.captcha')}
							disabled={typeof captchaQuestion !== 'string'}
						/>
					</label>

					<label className={modClasses('stade-stars-form__field', 'submit')}>
						<span className="visuallyhidden">
							{translate('ratings.submit')}:
						</span>

						<input
							className="stade-stars-form__field-input"
							type="submit"
							value={translate(captchaRetryEnabled ? 'ratings.captcha-loading-retry' : 'ratings.submit')}
							disabled={!submitEnabled && !captchaRetryEnabled}
						/>
					</label>
				</form>
			</div>
		</Fragment>);
	}
);
